import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { SortDirectionEnterprise } from '@lsq-frontend/angular-shared/api-service';
import { ListBuyersForOperationsService } from '@lsq-frontend/remote-services/all-java/financing-mk1-services';
import {
    ClassNames,
    Color,
    ContentTypes,
    forLifeOf,
    getRouterLinkUrlById,
    LifecycleAware,
    LsqFilePickerComponent,
    LsqSearchComponent,
    Modal
} from '@lsq-frontend/common-ui';
import { Icons } from '@lsq-frontend/common-ui';
import { ModalWorkflowService } from '@lsq-frontend/common-ui';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { FileUpload, HttpMethod, Uploader } from 'rxjs-uploader';
import { distinctUntilKeyChanged, first, map, shareReplay } from 'rxjs/operators';
import { ADMIN_SUPPLIERS, atlas } from '@lsq-frontend/fasttrack-shared/routing-atlas';
import { VerifySupplierUploadModalComponent } from '../modals/verify-supplier-upload-modal.component';
import { CreateSupplierService } from './create-suppliers.service';
import { AlertService } from '@lsq-frontend/angular-shared/features/alert-service';

@Component({
    selector: 'create-suppliers',
    styleUrls: ['./create-suppliers.component.scss'],
    template: `
        <div class="create-suppliers">
            <page-header
                titleText="Add New Suppliers"
                backButtonText="Back To Suppliers"
                (handleBackButtonClick)="navigateToSuppliersPage()"
            >
            </page-header>
            <div class="container-fluid page-container">
                <div class="row">
                    <div class="col-12 col-lg-8" [style.marginBottom]="'2rem'">
                        <card [hasHeader]="false">
                            <div card-body [style.padding]="'3rem'">
                                <div class="section">
                                    <h2 class="title">Upload Spreadsheet</h2>
                                    <p class="body" [style.marginBottom]="'2rem'">
                                        First, find and select the proper Buyer that all of the
                                        suppliers in the CSV will be assigned to.
                                    </p>
                                    <lsq-label
                                        label="Set Buyer Relationship"
                                        subLabel="Select the buyer relationship for the suppliers in the spreadsheet."
                                    ></lsq-label>
                                    <form
                                        [formGroup]="formGroup"
                                        [style.display]="selectedBuyerKey ? 'none' : 'block'"
                                        [style.marginBottom]="'3rem'"
                                    >
                                        <lsq-search
                                            #buyerSearch
                                            placeholder="Search Buyers"
                                            formControlName="buyerSearch"
                                            [name]="'buyerSearch'"
                                            [group]="formGroup"
                                            primaryDisplayKey="buyerLegalName"
                                            [primaryKey]="'buyerKey'"
                                            [searchService]="listBuyersForOperationsService"
                                            requestSearchTextFieldName="filter.buyerPreferredName"
                                            useBaseFilterRequest="true"
                                            [baseFilterRequest]="baseFilterRequest"
                                        >
                                        </lsq-search>
                                    </form>
                                    <data-card
                                        [style.marginBottom]="'3rem'"
                                        *ngIf="selectedBuyerKey"
                                    >
                                        <p class="label" [style.marginBottom]="'0'">
                                            Buyer Relationship
                                        </p>
                                        <p class="body" [style.marginBottom]="'1rem'">
                                            {{ selectedBuyerName }}
                                        </p>
                                        <lsq-button
                                            appearance="transparent"
                                            (buttonClick)="clearSelectedBuyerFields()"
                                        >
                                            Edit
                                        </lsq-button>
                                    </data-card>
                                </div>
                                <p class="body" [style.marginBottom]="'1rem'">
                                    Use the field below to attach your supplier data in CSV format.
                                </p>
                                <div>
                                    <a (click)="downloadTemplateFile()">
                                        Download sample template file
                                    </a>
                                </div>
                                <br />
                                <div>
                                    <p class="label" [style.marginBottom]="'1rem'">
                                        Attach CSV File
                                    </p>
                                    <lsq-file-picker
                                        [placeholderText]="buttonText"
                                        placeholder="Attach Supplier CSV"
                                        [name]="'supplierFileUpload'"
                                        [uploadErrorMessage]="uploadErrorMessage"
                                        [fileUpload]="fileUpload$ | async"
                                        (fileSourceValueChanged)="handleFileSelected($event)"
                                        (fileSourceElementChanged)="setFileSource($event)"
                                    >
                                    </lsq-file-picker>
                                    <lsq-button
                                        #uploadButton
                                        *ngIf="!(fileUpload$ | async)?.uploading"
                                        appearance="primary"
                                        size="md"
                                        [disabled]="
                                            !hasFileSelected ||
                                            uploadErrorMessage ||
                                            !selectedBuyerKey
                                        "
                                        (buttonClick)="this.showVerifySupplierModal()"
                                    >
                                        Continue
                                    </lsq-button>
                                </div>
                            </div>
                        </card>
                    </div>
                    <div class="col-12 col-lg-4">
                        <data-card [lightBorder]="true">
                            <p class="header" [style.marginBottom]="'1rem'">
                                Additional Information
                            </p>
                            <p class="body text-dark-gray" [style.marginBottom]="'1rem'">
                                This step will upload all of the listed suppliers within the
                                spreadsheet CSV file into the system as long as the rows and columns
                                are correctly constructed.
                            </p>
                        </data-card>
                    </div>
                </div>
            </div>
        </div>
    `
})
@LifecycleAware()
export class CreateSuppliersComponent implements OnInit, OnDestroy {
    @ViewChild('buyerSearch') public buyerSearch: LsqSearchComponent;
    @ViewChild('uploadButton') public uploadButtonRef: ElementRef;
    @ViewChild(LsqFilePickerComponent) public lsqFilePickerComponent: LsqFilePickerComponent;
    public icons = Icons;
    public colors = Color;
    public hasFileSelected = false;
    public fileUpload$: Observable<FileUpload>;
    public uploadButtonClickSubject = new Subject();
    public uploadErrorMessage: string;

    public fileName: string;
    public uploadUrl: string;
    public buttonText = 'Attach Supplier CSV';

    public formGroup: FormGroup = new FormGroup({
        buyerSearch: new FormControl(null, [])
    });
    public selectedBuyerKey: string;
    public selectedBuyerName: string;

    public baseFilterRequest = {
        skip: 0,
        take: 25,
        sortColumn: 'createdAt',
        sortDirection: SortDirectionEnterprise.Desc,
        filter: {
            buyerPreferredName: ''
        }
    };

    private readonly uploadWrongFileTypeErrorMessage = 'Your file upload must be in CSV format.';
    private readonly contentSizeErrorMessage = 'Your file exceeds the 100mb limit.';
    private readonly genericErrorMessage =
        'The upload was not successful, please try again and if the problem persists contact support at';
    private readonly _fastTrackEmailAddress = 'fasttrack-support@lsq.com';
    private readonly _errorMessage = `${this.genericErrorMessage}<a href="mailto:${this._fastTrackEmailAddress}" class="mail-to preserve-case">${this._fastTrackEmailAddress}</a>`;
    constructor(
        private _router: Router,
        private _createSuppliersService: CreateSupplierService,
        private _modalWorkflowService: ModalWorkflowService,
        private _alertService: AlertService,
        public listBuyersForOperationsService: ListBuyersForOperationsService
    ) {
        this.fileUploader.errorStream.pipe(forLifeOf(this)).subscribe((error) => {
            if (
                error.message === this.uploadWrongFileTypeErrorMessage ||
                error.message === this.contentSizeErrorMessage
            ) {
                this.uploadErrorMessage = error.message;
            } else {
                this.uploadErrorMessage = this._errorMessage;
            }
        });
    }

    public fileUploader: Uploader = new Uploader<FileUpload>({
        allowedContentTypes: ContentTypes.Csvs,
        requestOptions: {
            url: '',
            headers: {
                'Content-Type': 'text/csv'
            }
        },
        fileSizeLimitMb: 100,
        fileCountLimit: 1,
        requestUrl: '',
        uploadFileAsBody: true,
        disallowedContentTypeErrorMessage: () => this.uploadWrongFileTypeErrorMessage,
        disallowedContentSizeErrorMessage: () => this.contentSizeErrorMessage,
        allFilesQueuedCallback: async (fileUploads) => {
            try {
                this.fileName = fileUploads[0].name;
                await this.uploadButtonClickSubject.pipe(first()).toPromise();
                this.fileUploader.patchRequestOptions({
                    method: HttpMethod.Put
                });

                const presignedUrl = await this._getPresignedUrl();
                const uri = presignedUrl.split('?')[0].split('/');
                const fileName = uri
                    .slice(Math.max(uri.length - 4, 0))
                    .join('/')
                    .trim();
                const newFile = this._renameFile(fileUploads, fileName);
                this.fileUploader.setRequestUrl(presignedUrl);
                return [new FileUpload(newFile)];
            } catch (e) {
                this._handleFileUploadError();
            }
            return null;
        },
        fileUploadedCallback: (fileUpload) => {
            if (fileUpload.succeeded) {
                this._modalWorkflowService.nextStep();
            }

            if (fileUpload.failed) {
                this._handleFileUploadError();
            }
        }
    });

    private _handleFileUploadError() {
        this._modalWorkflowService.hideWorkflow();
        this.lsqFilePickerComponent.removeFile();
        this._alertService.error('File failed to upload. Please try again.');
    }

    private _renameFile(fileUploads: FileUpload[], fileName: string): File {
        const fileUpload = fileUploads[0];
        const file = fileUpload.file;
        const blob = file.slice(0, file.size, ContentTypes.Csv);
        const newFile = new File([blob], fileName, { type: ContentTypes.Csv });
        return newFile;
    }

    public async downloadTemplateFile(): Promise<void> {
        await this._createSuppliersService.downloadSuppliersUploadTemplate();
    }

    public ngOnInit(): void {
        this.formGroup.valueChanges
            .pipe(forLifeOf(this), distinctUntilKeyChanged('buyerSearch'))
            .subscribe(async (value) => {
                if (value.buyerSearch) {
                    this.selectedBuyerKey = value.buyerSearch;
                    this.selectedBuyerName = this.buyerSearch.getSelectedResultPrimaryDisplayText();
                } else {
                    this.selectedBuyerKey = null;
                    this.selectedBuyerName = null;
                }
            });
    }

    public async navigateToSuppliersPage(): Promise<void> {
        await this._router.navigateByUrl(getRouterLinkUrlById(atlas, ADMIN_SUPPLIERS));
    }

    public setFileSource(fileSourceElement: ElementRef): void {
        this.fileUpload$ = this.fileUploader
            .streamFileUploads(fileSourceElement.nativeElement as HTMLElement)
            .pipe(
                map(([fileUpload]) => fileUpload),
                shareReplay(1)
            );
    }

    public handleFileSelected(fileRef: string): void {
        this.hasFileSelected = !!fileRef;
        this.uploadErrorMessage = null;
    }

    private async _getPresignedUrl(): Promise<string> {
        return this._createSuppliersService.getSuppliersUploadPresignedUrl(this.selectedBuyerKey);
    }

    public showVerifySupplierModal(): void {
        const isSubmittingSubject = new BehaviorSubject<boolean>(false);
        const fileName = this.fileName;

        this._modalWorkflowService.showWorkflow([
            new Modal({
                id: 'verify-supplier-upload-modal',
                title: 'Confirm Add Supplier',
                bodyComponent: VerifySupplierUploadModalComponent,
                showFooter: true,
                showOkButton: true,
                showCancelButton: true,
                showCloseButton: true,
                okButtonSize: ClassNames.Block,
                okButtonText: 'Confirm and Upload',
                isSubmitting$: isSubmittingSubject,
                data: { fileName: fileName, buyerName: this.selectedBuyerName },

                onOkClick: async () => {
                    isSubmittingSubject.next(true);
                    this.uploadButtonClickSubject.next();
                    isSubmittingSubject.next(false);
                },

                onCancelClick: () => this._modalWorkflowService.hideWorkflow()
            }),
            new Modal({
                title: 'Upload Successful',
                bodyHtml: `<div class="modal-body toggle-supplier-eligibility-modal">
                Thanks for uploading the new supplier data. Please allow 10-15 minutes for the file to be processed properly and for the suppliers to begin showing in the system.
                </div>`,
                showCancelButton: false,
                showFooter: true,
                showOkButton: true,
                okButtonText: 'Back to Suppliers',
                onOkClick: async () => {
                    await this._router.navigateByUrl(getRouterLinkUrlById(atlas, ADMIN_SUPPLIERS));
                }
            })
        ]);
    }

    public clearSelectedBuyerFields() {
        this.selectedBuyerKey = null;
        this.selectedBuyerName = null;
        this.buyerSearch.clearSearchAndValue();
    }

    public ngOnDestroy(): void {}
}
